import React, { useContext, useEffect, useMemo, useState } from "react";

import { ZeosApiContext } from "../../../context/ZeosApiContext";
import { IAdminCustomerResponse } from "../../../services/zeos-api/contracts/Admin";
import { IUapiAttachResponse } from "../../../services/zeos-api/contracts/Uapi";

// MTR-P1-01 (task-4054): link a UtilityAPI authorization made outside a ZEOS form (e.g. on the UtilityAPI
// dashboard) to a property and sync its meters. Shown in the super-admin metering tab; the API enforces the
// same rule. Safe to repeat: a second run only re-syncs. New meters land UNASSIGNED in the queue below.

const isSuccess = (status?: string) => status?.toLowerCase() === "success";

interface IPropertyOption {
  propertyId: number;
  label: string;
}

const toOptions = (customers: IAdminCustomerResponse[]): IPropertyOption[] =>
  customers
    .flatMap((customer) =>
      customer.companies.flatMap((company) =>
        company.properties
          .filter((property) => !property.isSandbox)
          .map((property) => ({
            propertyId: property.propertyId,
            label: `${company.name} · ${property.name} (ID ${property.propertyId})`,
          }))
      )
    )
    .sort((a, b) => a.label.localeCompare(b.label));

interface IAttachUtilityAuthorizationProps {
  /** Called after a successful attach, e.g. to refresh the unassigned-meters list. */
  onAttached?: () => void;
}

export const AttachUtilityAuthorization = ({ onAttached }: IAttachUtilityAuthorizationProps) => {
  const api = useContext(ZeosApiContext);
  const [customers, setCustomers] = useState<IAdminCustomerResponse[]>([]);
  const [authorizationUid, setAuthorizationUid] = useState("");
  const [propertyId, setPropertyId] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<IUapiAttachResponse | null>(null);

  useEffect(() => {
    const loadPropertiesAsync = async () => {
      try {
        const response = await api.Admin.getCustomersAsync();
        if (isSuccess(response.zeosStatus)) {
          setCustomers(response.data ?? []);
        } else {
          setError(response.message || "Could not load properties.");
        }
      } catch {
        setError("Could not load properties.");
      }
    };
    loadPropertiesAsync();
  }, [api]);

  const options = useMemo(() => toOptions(customers), [customers]);
  const canAttach = authorizationUid.trim() !== "" && propertyId !== "" && !saving;

  const attach = async () => {
    if (!canAttach) {
      return;
    }
    setSaving(true);
    setError("");
    setResult(null);
    try {
      const response = await api.Uapi.attachAuthorization({
        authorizationUid: authorizationUid.trim(),
        propertyId: Number(propertyId),
      });
      if (isSuccess(response.zeosStatus) && response.data) {
        setResult(response.data);
        onAttached?.();
      } else {
        setError(response.message || "Could not attach the authorization.");
      }
    } catch {
      setError("Could not attach the authorization.");
    } finally {
      setSaving(false);
    }
  };

  const sync = result?.sync;

  return (
    <div className="bg-white border border-gray-200 rounded p-4">
      <h3 className="text-sm font-semibold text-black mb-1">Attach a UtilityAPI authorization</h3>
      <p className="text-xs text-gray-600 mb-3">
        For authorizations made outside the app. Creates the property's meters from UtilityAPI as Unassigned;
        running it again only refreshes them.
      </p>
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-xs text-gray-700">
          Authorization UID
          <input
            type="text"
            aria-label="Authorization UID"
            className="block w-40 mt-1 text-sm border border-gray-300 rounded px-2 py-1"
            value={authorizationUid}
            onChange={(e) => setAuthorizationUid(e.target.value)}
            disabled={saving}
          />
        </label>
        <label className="text-xs text-gray-700">
          Property
          <select
            aria-label="Property"
            className="block w-80 mt-1 text-sm border border-gray-300 rounded px-2 py-1"
            value={propertyId}
            onChange={(e) => setPropertyId(e.target.value)}
            disabled={saving}
          >
            <option value="">Choose…</option>
            {options.map((option) => (
              <option key={option.propertyId} value={option.propertyId}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className="text-sm text-zeos-orange-dk hover:underline disabled:opacity-50"
          onClick={attach}
          disabled={!canAttach}
        >
          {saving ? "Attaching…" : "Attach"}
        </button>
      </div>
      {error && (
        <p role="alert" className="text-xs text-red-600 mt-3">
          {error}
        </p>
      )}
      {result && sync && (
        <div role="status" className="text-xs text-gray-700 mt-3 space-y-1">
          <p className="font-semibold">
            {result.newlyAttached ? "Attached." : "Already attached; meters refreshed."}{" "}
            {sync.created.length} created, {sync.updated.length} updated, {sync.retired.length} retired.
          </p>
          {sync.serviceProfilesCreated.length > 0 && (
            <p>New services to confirm: {sync.serviceProfilesCreated.join(", ")}.</p>
          )}
          {sync.monitoringRequested.length > 0 && (
            <p>Monthly monitoring requested for {sync.monitoringRequested.length} meter(s).</p>
          )}
          {sync.skipped.length > 0 && (
            <div>
              <p>Skipped:</p>
              <ul className="list-disc ml-5">
                {sync.skipped.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
